// application/useCases/loadCoverageSession.js

import { unreadablePolicyholder } from '../../domain/model/PolicyholderDemographics';

/**
 * Reads everything the coverage editor needs for one client.
 *
 * A use case rather than two calls in the composable, for symmetry with `commitCoverages`: both
 * ends of the session go through the application layer against named ports, so the UI never talks
 * to a repository directly and neither read is somewhere a rule can quietly accumulate.
 *
 * The rule it owns today: a client that does not exist yet has no coverages to read, but the
 * payers still load — coverage can be staged for a client being created, and the flush stamps the
 * id afterwards. Returning an empty list rather than skipping the load keeps that case ordinary
 * for the caller.
 *
 * @param {{ clientId: string|null, clinicId: string, asOf: string }} scope
 * @param {Object} repositories
 * @param {import('../ports/CoverageRepository.port').CoverageRepository}
 *   repositories.coverageRepository
 * @param {import('../ports/FundingSourceRepository.port').FundingSourceRepository}
 *   repositories.fundingSourceRepository
 * @param {import('../ports/PersonRepository.port').PersonRepository}
 *   repositories.personRepository
 * @returns {Promise<{
 *   coverages: import('../../domain/model/Coverage.types').Coverage[],
 *   openBillingIntents: Array<{ id: string, coverageId: string }>,
 *   fundingSources: import('../../domain/model/FundingSource.types').FundingSource[],
 *   people: Record<string, import(
 *     '../../domain/model/PolicyholderDemographics').PolicyholderDemographics>,
 * }>}
 */
// eslint-disable-next-line import/prefer-default-export
export async function loadCoverageSession(
  { clientId, clinicId, asOf },
  { coverageRepository, fundingSourceRepository, personRepository },
) {
  const hasClient = clientId !== null && clientId !== undefined;

  const [fundingSources, coverages, openBillingIntents] = await Promise.all([
    // `asOf` is what resolves each payer to the version that applies today. Omitting it used to be
    // harmless, and stopped being so when funding sources became effective-dated: the picker would
    // silently drop every funder that had ever been amended, because a series with several versions
    // resolves to none without a date to resolve against.
    fundingSourceRepository.loadFundingSources(clinicId, asOf),
    hasClient ? coverageRepository.loadClientCoverages({ clientId, clinicId }) : Promise.resolve([]),
    hasClient
      ? coverageRepository.loadOpenBillingIntents({ clientId, clinicId })
      : Promise.resolve([]),
  ]);

  /**
   * Whoever holds a policy on any of these coverages, keyed by id, so a row can say whether it is
   * ready to bill without asking again per row.
   *
   * A SECOND ROUND TRIP, unavoidably: which people to read is a property of the coverages, so it
   * cannot be started until they arrive. It is one query for all of them, and the client is in it
   * because a self-policyholder coverage asks the most of exactly that record.
   *
   * The clinic goes with the ids. They come off stored coverages, so nothing between here and the
   * database has re-checked which clinic those people belong to.
   */
  async function loadPeople(loadedCoverages) {
    const wanted = [...new Set(loadedCoverages
      .filter((coverage) => coverage.isDeleted !== true)
      .map((coverage) => coverage.policyholder.personId ?? clientId)
      .filter(Boolean))];

    if (wanted.length === 0) {
      return {};
    }

    const found = new Map((await personRepository.loadPeople({ personIds: wanted, clinicId }))
      .map((person) => [person.personId, person]));

    // Every id asked for comes back with something. Leaving an unresolved one out would hand the
    // consumer nothing, which readiness reads as "still loading" and stays quiet about, so a
    // coverage naming a person who is gone would look no different from one that is fine. The
    // editor already says "Policyholder not found" for that record, and the row has to agree.
    return Object.fromEntries(wanted
      .map((personId) => [personId, found.get(personId) ?? unreadablePolicyholder(personId)]));
  } // loadPeople()

  return { coverages, openBillingIntents, fundingSources, people: await loadPeople(coverages) };
} // loadCoverageSession()
